import { getErrorStatus } from '../helpers/formatters';
import { getGlobalState, messagesSelector } from '../helpers/selectors';
import { addErrorMsg } from '../utils/flashMessage/creator';
import { jsonParseSafe, safe } from '../utils/utils';
import { STORAGE_KEY, VERIFY_STATUS } from '../constants/generic';

// [KC-DEBUG] This module-evaluation line below is the ONLY READ of localStorage
// KEYCLOAK_CONFIG in the whole codebase, and the setItem in GET_REMOTE_CONFIG_SUCCESS
// is the ONLY WRITE. The mirror read here exists purely to log what that read sees.
const kcDebugStoredRaw = safe(() => localStorage.getItem(STORAGE_KEY.KEYCLOAK_CONFIG));
const kcDebugStoredParsed = jsonParseSafe(kcDebugStoredRaw);

console.log('[KC-DEBUG] reducers/keycloak.js/module-init: READ localStorage KEYCLOAK_CONFIG (ONLY read; runs once at module load, before any brand config is fetched)', { present: kcDebugStoredRaw !== null && kcDebugStoredRaw !== undefined, rawType: typeof kcDebugStoredRaw, rawLength: kcDebugStoredRaw?.length, parsedOk: Boolean(kcDebugStoredParsed), parsedType: typeof kcDebugStoredParsed, parsedKeys: kcDebugStoredParsed ? Object.keys(kcDebugStoredParsed).join(',') : null, superCompanyId: kcDebugStoredParsed?.superCompanyId, clientId: kcDebugStoredParsed?.clientId, realm: kcDebugStoredParsed?.realm, identityProvider: kcDebugStoredParsed?.identityProvider, storageReadable: kcDebugStoredRaw !== undefined, note: 'absent => state.keycloak.remoteConfig starts undefined => parseKeycloakConfig gets be=undefined' }); // eslint-disable-line no-console
console.log('[KC-DEBUG] reducers/keycloak.js/module-init: TYPE MISMATCH WATCH - this read yields an OBJECT (jsonParseSafe), while GET_REMOTE_CONFIG_SUCCESS later puts a STRING into the same state slot', { readPathType: typeof kcDebugStoredParsed, writePathTypeWillBe: 'string', sameStateKey: 'state.keycloak.remoteConfig' }); // eslint-disable-line no-console

const defaultState = {
  // Keycloak has let someone in and we have no member for him: GET /me answered 404.
  // That and nothing else - it ends the moment a member turns up, whichever call
  // produced him - so whatever ACTS on it can read it straight: may this visitor be
  // in /register/* at all, and does the first step register him or update him.
  isExternalIncompleteUser: false,
  // null until kc.init() has answered - "we do not know yet", which is not the same
  // as UNAUTH, and stays null for a brand that never inits Keycloak at all.
  initStatus: null,
  // What GET /me has answered about this session, one of VERIFY_STATUS. Anything
  // that reads "Keycloak has a session but this visitor got no access" needs it:
  // initStatus AUTH plus an empty user slice is equally true while the answer is
  // still in flight, and equally true when the backend never managed to give one.
  verifyStatus: VERIFY_STATUS.IDLE,
  remoteConfig: jsonParseSafe(localStorage.getItem(STORAGE_KEY.KEYCLOAK_CONFIG))
};

// What a GET /me failure means, keyed by the HTTP status it came back with. The
// message the visitor is shown and the outcome the rest of the app reads are two
// faces of one decision, so they sit in one row instead of in two parallel chains.
const VERIFY_FAILURE_BY_STATUS = {
  401: { messageId: 'error.user.verify.fail', status: VERIFY_STATUS.REFUSED },
  403: { messageId: 'error.login.403.user.anonymized', status: VERIFY_STATUS.REFUSED },
  422: { messageId: 'error.login.422.user.not.enabled', status: VERIFY_STATUS.REFUSED },
  404: { status: VERIFY_STATUS.NO_MEMBER }
};

// Everything the table does not name: a 5xx, a gateway timeout, a connection that
// dropped - the last of those reaches the reducer with no `response` at all, hence no
// status to key by. None of it is a verdict on the account, only the absence of one,
// and that is the distinction the registration guard acts on: it ends the Keycloak
// session over a REFUSED, and a five-second outage must not cost a visitor a session
// he still owns.
const VERIFY_FAILURE_TECHNICAL = {
  messageId: 'error.form.technical',
  status: VERIFY_STATUS.FAILED
};

const readVerifyFailure = (action) =>
  VERIFY_FAILURE_BY_STATUS[getErrorStatus(action.payload)] || VERIFY_FAILURE_TECHNICAL;

// Where GET /me stands, as the actions that move it along - resolvers rather than
// plain values, because a failure is not one outcome but the several above.
//
// The two answering entries are deliberate about WHICH action they sit on. The raw
// keycloak/VERIFY_USER_FAIL from the axios middleware would be too early: it lands a
// microtask before App.verifyExternalLogin decides what the failure means, so a render
// in between would show an answer while isExternalIncompleteUser is still unset.
// keycloak/VERIFY_FAILED and keycloak/VERIFY_USER_SUCCESS are the actions that carry
// that decision (the 404 flag here, the member in reducers/user.js), so marking the
// answer on them keeps the status and its consequence in the same single dispatch.
const VERIFY_STATUS_BY_ACTION = {
  'keycloak/VERIFY_USER': () => VERIFY_STATUS.PENDING,
  'keycloak/VERIFY_USER_SUCCESS': () => VERIFY_STATUS.MEMBER,
  'keycloak/VERIFY_FAILED': (action) => readVerifyFailure(action).status
};

// Nobody is signed in any more. reducers/user.js empties its own slice on these three;
// the flag of this one left standing would keep RegisterAccessGuard letting a visitor
// with no token into /register/*.
const SESSION_ENDED_BY = [
  'user/LOGOUT',
  'user/SELF_ANONYMIZE_SUCCESS',
  'user/FINAL_ACCESS_DENIED'
];

// A member exists behind the session now: /me found one, or one of the two
// registration calls has just made one. Whichever it was, "Keycloak is in and we have
// nobody" has stopped being true - including when the /me that PersonalInfo fires
// after registering never answers, which is the whole reason the registration itself
// counts here and not just the user slice it eventually fills.
const MEMBER_EXISTS_AFTER = [
  'keycloak/VERIFY_USER_SUCCESS',
  'user/REGISTER_SSO_SUCCESS',
  'user/LINK_PROFILE_SUCCESS'
];

const EXTERNAL_INCOMPLETE_USER_ENDED_BY = new Set([
  ...MEMBER_EXISTS_AFTER,
  ...SESSION_ENDED_BY
]);

// One flag, one action later: an action that raises it wins, and otherwise it stands
// until one of the actions that end it goes by. The flag is never dispatched for
// directly - it is read off actions sent for other reasons - so raise and end only
// mean anything stated side by side.
const nextFlag = (current, { raisedBy, endedBy }) => raisedBy || (current && !endedBy);

// A new state object only where something actually moved: this reducer sees every
// action in the app, and spreading on each one would wake every subscriber.
const withChanges = (state, changes) =>
  Object.keys(changes).some((key) => state[key] !== changes[key])
    ? { ...state, ...changes }
    : state;

export default function reducer(state = defaultState, action) {
  // What THIS action says about GET /me, or undefined for the vast majority that say
  // nothing at all. Read once and used twice: the 404 that moves the status to
  // NO_MEMBER is also the one and only thing that raises isExternalIncompleteUser.
  const verifyAnswer = VERIFY_STATUS_BY_ACTION[action.type]?.(action);

  state = withChanges(state, {
    verifyStatus: verifyAnswer ?? state.verifyStatus,
    isExternalIncompleteUser: nextFlag(state.isExternalIncompleteUser, {
      raisedBy: verifyAnswer === VERIFY_STATUS.NO_MEMBER,
      endedBy: EXTERNAL_INCOMPLETE_USER_ENDED_BY.has(action.type)
    })
  });

  switch (action.type) {
    case 'keycloak/VERIFY_FAILED': {
      const globalState = getGlobalState();
      const messages = messagesSelector(globalState);
      const { messageId } = readVerifyFailure(action);
      const errorMsg = messages[messageId];

      console.log('[KC-DEBUG] reducers/keycloak.js/VERIFY_FAILED: /me failed - branching on HTTP status (404 => isExternalIncompleteUser=true => registration flow; anything else => error message, user stays logged out)', { httpStatus: getErrorStatus(action.payload) }); // eslint-disable-line no-console

      if (errorMsg) addErrorMsg(errorMsg);

      break;
    }

    case 'keycloak/SET_INIT_STATUS': {
      console.log('[KC-DEBUG] reducers/keycloak.js/SET_INIT_STATUS: kc.init() outcome recorded', { from: state.initStatus, to: action.payload, toName: { 0: 'AUTH', 1: 'UNAUTH', 2: 'FAIL' }[action.payload] }); // eslint-disable-line no-console
      state = { ...state, initStatus: action.payload };
      break;
    }

    case 'keycloak/GET_REMOTE_CONFIG_SUCCESS': {
      const remoteConfig = JSON.stringify(action.payload.data);

      console.log('[KC-DEBUG] reducers/keycloak.js/GET_REMOTE_CONFIG_SUCCESS: storing remoteConfig as a STRING (parseKeycloakConfig later reads it as an object - see remoteConfigIsString in its ENTER log)', { superCompanyId: action.payload.data?.superCompanyId, clientId: action.payload.data?.clientId, realm: action.payload.data?.realm, identityProvider: action.payload.data?.identityProvider }); // eslint-disable-line no-console
      console.log('[KC-DEBUG] reducers/keycloak.js/GET_REMOTE_CONFIG_SUCCESS: WRITING localStorage KEYCLOAK_CONFIG (ONLY write in the codebase - it is reached ONLY after /me returns 404, which needs a successful kc.init() first)', { keyName: STORAGE_KEY.KEYCLOAK_CONFIG, valueType: typeof remoteConfig, valueLength: remoteConfig?.length, payloadDataType: typeof action.payload.data, payloadKeys: action.payload.data ? Object.keys(action.payload.data).join(',') : null }); // eslint-disable-line no-console
      localStorage.setItem(STORAGE_KEY.KEYCLOAK_CONFIG, remoteConfig);
      console.log('[KC-DEBUG] reducers/keycloak.js/GET_REMOTE_CONFIG_SUCCESS: WRITE READBACK (if writtenLength is null the storage write was silently rejected, e.g. partitioned/blocked storage)', { writtenLength: safe(() => localStorage.getItem(STORAGE_KEY.KEYCLOAK_CONFIG))?.length ?? null, writeSucceeded: safe(() => localStorage.getItem(STORAGE_KEY.KEYCLOAK_CONFIG)) === remoteConfig, stateRemoteConfigTypeNow: typeof remoteConfig }); // eslint-disable-line no-console
      state = { ...state, remoteConfig };

      break;
    }

    case 'keycloak/GET_REMOTE_CONFIG_FAIL': {
      console.log('[KC-DEBUG] reducers/keycloak.js/GET_REMOTE_CONFIG_FAIL: /v2/companies/sso-registration failed => KEYCLOAK_CONFIG is NOT written', { httpStatus: getErrorStatus(action.payload) }); // eslint-disable-line no-console
      break;
    }

    case 'keycloak/VERIFY_USER_SUCCESS': {
      console.log('[KC-DEBUG] reducers/keycloak.js/VERIFY_USER_SUCCESS: /me succeeded - the member exists, no SSO registration needed', { userId: action.payload?.data?.id, memberStatus: action.payload?.data?.status, hasCompany: Boolean(action.payload?.data?.company) }); // eslint-disable-line no-console
      break;
    }

    default: {
      break;
    }
  }

  return state;
}
